<div [class]="fullWidth ? 'w-full p-4' : 'max-w-4xl mx-auto p-4'">

  <!-- Filter toolbar -->
  <form [formGroup]="filterForm" class="flex flex-wrap gap-3 mb-5 items-center">

    <mat-form-field appearance="outline" class="w-48">
      <mat-label>Status</mat-label>
      <mat-select formControlName="status">
        <mat-option value="">All</mat-option>
        <mat-option value="DRAFT">Draft</mat-option>
        <mat-option value="FINALISED">Finalised</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="w-56">
      <mat-label>Inspector</mat-label>
      <input matInput formControlName="inspectorSearch" placeholder="Search by name..." />
    </mat-form-field>

    <button
      type="button"
      mat-stroked-button
      class="self-center"
      (click)="clearFilters()">
      Clear
    </button>

    <!-- New Inspection only shown in pin context: an inspector fills the report in, a requestor
         picks an inspector and saves the request -->
    <button
      *ngIf="!hidePinRequiredActions && (helper.canCreateInspectionReport() || helper.canRequestInspection())"
      type="button"
      mat-flat-button
      color="primary"
      class="self-center"
      (click)="createNewInspection.emit()">
      <mat-icon>add</mat-icon>
      New Inspection
    </button>

  </form>

  <!-- No pin context hint -->
  <div
    *ngIf="hidePinRequiredActions"
    class="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100">
    <mat-icon class="text-amber-500 text-[16px]">info</mat-icon>
    <span class="text-xs text-amber-700">
      To create a new inspection, drop a Field Inspection pin on the map.
    </span>
  </div>

  <!-- Loading -->
  <div *ngIf="inspectionService.loading()" class="text-sm text-gray-400 italic py-4">
    Loading reports...
  </div>

  <!-- Empty state -->
  <div
    *ngIf="!inspectionService.loading() && seasonGroups.length === 0"
    class="text-sm text-gray-400 italic py-8 text-center">
    {{ accessDenied ? 'You do not have access to these inspection reports.' : 'No inspection reports found.' }}
  </div>

  <!-- Season groups -->
  <div *ngFor="let group of seasonGroups" class="mb-8">

    <div class="flex items-center gap-2 mb-3">
      <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide border-b pb-2">
        {{ group.seasonName || 'Historical' }}
      </div>
      <span *ngIf="group.isActive"
            class="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
        Active
      </span>
      <span *ngIf="!group.isActive && group.seasonId !== null"
            class="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
        Ended
      </span>
      <span class="text-xs text-gray-400">({{ group.reports.length }} inspection{{ group.reports.length === 1 ? '' : 's' }})</span>
    </div>

    <div class="border border-gray-200 rounded overflow-hidden">
      <div
        *ngFor="let report of group.reports; let last = last"
        (click)="openReport(report)"
        class="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
        [class.border-b]="!last"
        [class.border-gray-100]="!last">

        <!-- Status badge -->
        <span
          class="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
          [ngClass]="getStatusClass(report.status)">
          {{ report.status === 'FINALISED' ? 'Finalised' : 'Draft' }}
        </span>

        <!-- Date -->
        <span class="text-sm text-gray-700 flex-shrink-0 w-36">
          {{ report.inspectionTimestamp | date:'dd MMM yyyy HH:mm' }}
        </span>

        <!-- Field / Farm — shown in farm/tenant scope -->
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-700 truncate">{{ report.fieldName }}</p>
          <p *ngIf="report.farmName" class="text-xs text-gray-400 truncate">{{ report.farmName }}</p>
        </div>

        <!-- Inspector -->
        <span class="text-sm text-gray-500 flex-shrink-0 hidden sm:block">
          {{ report.inspectorName }}
        </span>

        <!-- Delete (DRAFT only) -->
        <button
          *ngIf="report.status === 'DRAFT' && helper.canEditInspection()"
          type="button"
          mat-icon-button
          class="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
          matTooltip="Delete draft"
          (click)="deleteReport(report, $event)">
          <mat-icon>delete_outline</mat-icon>
        </button>

        <!-- Re-send PDF (FINALISED only) -->
        <button
          *ngIf="report.status === 'FINALISED' && helper.canEditInspection()"
          type="button"
          mat-icon-button
          class="flex-shrink-0 text-gray-400 hover:text-[var(--primary-dark-variant)] transition-colors"
          [disabled]="retriggeringId === report.id"
          matTooltip="Re-send PDF report by email"
          (click)="retriggerPdf(report, $event)">
          <mat-icon>{{ retriggeringId === report.id ? 'hourglass_empty' : 'forward_to_inbox' }}</mat-icon>
        </button>

        <!-- Arrow -->
        <span class="text-gray-300 flex-shrink-0">›</span>

      </div>
    </div>

  </div>

</div>
